import { useContext, useRef, useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import ScanDocumentModal from "components/ScanDocumentModal";

import { getCompanyPublicKeyResult } from "utils/encryptForCompany";
import dispatchChartService from "api/dispatchChartService";
import { IPendingReceptionItem } from "dto/Mailroom/IDispatchChart";
import { handleCreate } from "utils/uploadFile";
import { ENotificationType, notification } from "utils/notification";

import styles from "./outgoing.module.scss";

interface Props {
  item: IPendingReceptionItem;
  onClose: () => void;
  onDone: () => void;
}

interface ScanFile {
  uri: string;
  keyId: string | null;
  fileName: string;
}

const ReceptionModal = ({ item, onClose, onDone }: Props) => {
  const { mutate, isLoading } =
    dispatchChartService.useSaveReceptionConfirmation();

  const [date, setDate] = useState<string>(new Date().toLocaleDateString("sv-SE"));
  const [isReturned, setIsReturned] = useState(false);
  const [annotation, setAnnotation] = useState("");
  const [scan, setScan] = useState<ScanFile | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Skan zwrotki ZPO — szyfrowany kluczem publicznym profilu (jak skan karty
  // nadawczej w ProcessDispatchChartModal / CreateDossierModal).
  const uploadScan = async (file: File) => {
    setUploading(true);
    try {
      // Skan szyfrowany kluczem publicznym FIRMY (model kluczy firmowych) — RZUCA
      // (CompanyKeyMissingError), gdy firma nie ma jeszcze aktywnego klucza.
      const masterKey = await getCompanyPublicKeyResult();

      const r = await handleCreate(
        [file],
        undefined,
        masterKey,
        true,
        true,
        file.name,
        file.type || "application/pdf",
      );

      if (!r || !r[0]) {
        throw new Error("Upload nie zwrócił pliku.");
      }
      setScan({ uri: r[0].uri, keyId: r[0].keyId ?? null, fileName: file.name });
    } catch (e) {
      notification({
        type: ENotificationType.ERROR,
        title: "Błąd",
        text: (e as Error)?.message ?? "Nie udało się dołączyć skanu.",
      });
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadScan(file);
  };

  const submit = () => {
    mutate(
      {
        ParcelId: item.Id,
        ReceptionDate: new Date(date).toISOString(),
        IsReturned: isReturned,
        ReturnAnnotation: isReturned ? annotation || null : null,
        ScanFileUri: scan?.uri ?? null,
        ScanKeyId: scan?.keyId ?? null,
      },
      {
        onSuccess: () => {
          notification({
            type: ENotificationType.SUCCESS,
            title: isReturned ? "Zarejestrowano zwrot" : "Zarejestrowano odbiór",
            text: `Przesyłka ${item.ParcelNumber ?? ""} zaktualizowana.`,
          });
          onDone();
        },
        onError: () =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się zapisać potwierdzenia odbioru.",
          }),
      },
    );
  };

  return (
    <>
      <div className={styles.overlay} onMouseDown={onClose}>
        <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
          <div className={styles.modalTitle}>
            {isReturned
              ? "Rejestracja zwrotu"
              : "Rejestracja odbioru (zwrotka ZPO)"}
          </div>
          <div className={styles.modalBody}>
            <div className={styles.modalRow}>
              <span>Adresat:</span>
              <strong>{item.ReceiverName ?? "—"}</strong>
            </div>
            {item.ParcelNumber && (
              <div className={styles.modalRow}>
                <span>Sygnatura:</span>
                <strong>{item.ParcelNumber}</strong>
              </div>
            )}

            <div className={styles.field}>
              <label>{isReturned ? "Data zwrotu" : "Data odbioru"}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            <label className={styles.check}>
              <input
                type="checkbox"
                checked={isReturned}
                onChange={(e) => setIsReturned(e.target.checked)}
              />
              Przesyłka zwrócona (niedoręczona)
            </label>

            {isReturned && (
              <div className={styles.field}>
                <label>Adnotacja (powód zwrotu)</label>
                <input
                  type="text"
                  value={annotation}
                  onChange={(e) => setAnnotation(e.target.value)}
                  placeholder="np. adresat nieznany, nie podjęto w terminie"
                />
              </div>
            )}

            <div className={styles.field}>
              <label>
                Skan zwrotki ZPO {isReturned ? "" : "(potwierdzenie odbioru)"}
              </label>
              {scan ? (
                <div className={styles.noteRow}>
                  <span className={styles.noteName}>{scan.fileName}</span>
                  <HollowButton text="Usuń" onClick={() => setScan(null)} />
                </div>
              ) : (
                <div className={styles.noteActions}>
                  <HollowButton
                    text={uploading ? "Wgrywanie…" : "Wgraj plik"}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <HollowButton text="Skanuj" onClick={() => setShowScan(true)} />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    hidden
                    onChange={onPickFile}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <HollowButton text="Anuluj" onClick={onClose} />
            <Button
              text="Zapisz"
              onClick={submit}
              loading={isLoading}
              disabled={uploading || !date}
            />
          </div>
        </div>
      </div>

      {showScan && (
        <ScanDocumentModal
          defaultFileName={`zwrotka-${(item.ParcelNumber ?? item.Id).replace(
            /\//g,
            "-",
          )}`}
          handleClose={() => setShowScan(false)}
          onComplete={(pdf) => uploadScan(pdf)}
        />
      )}
    </>
  );
};

export default ReceptionModal;
